/*----------------------------------
- CATALOG (SSOT): HOME DEMO CALLOUTS (SAMPLE)
----------------------------------*/

/**
 * The callouts over the sample screenshots of Home's demo, tabs 2 to 5, per persona (docs/mockups/website-home-v3.html,
 * the panels' `.co` layers; mock parity web-frame-17). SAMPLE TEXT: every name, count and price below describes the
 * sample screenshot under it (the "Sample screen" tag), never the catalog. Positions are the mock's, in percent of the
 * screen.
 *
 * @docs docs/features/website-landing-experience
 * @rule Sample text only, about the sample screen it floats over; a time is written once and never ticks (the mock's
 *       countdown "2h 36m 12s" reads "2h 36m", no fake clock).
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import type { DemoCalloutTone } from '@uniquedomains/components/marketing/demo-window/DemoCallouts';
import type { HomePersona } from '@uniquedomains/components/marketing/demo-window/PersonaSwitch';

import type { SpriteIconName } from '@/client/components/ui/icon/Icon';
import type { HomeDemoSampleTabId } from '@/client/pages/landing/components/home/demoScreens';

/*----------------------------------
- TYPES
----------------------------------*/

export type HomeDemoCallout = {
    x: number;
    y: number;
    icon: SpriteIconName;
    tone?: DemoCalloutTone;
    before?: string;
    strong: string;
    /** Drawn as given: a leading space, or a comma where the mock writes one. */
    after?: string;
};

export type HomeDemoCalloutLayers = Partial<Record<HomePersona | 'shared', readonly HomeDemoCallout[]>>;

/*----------------------------------
- CATALOG (SSOT)
----------------------------------*/

export const HOME_DEMO_CALLOUTS: Record<HomeDemoSampleTabId, HomeDemoCalloutLayers> = {
    // One screen for both personas: the full analysis.
    check: {
        shared: [
            { x: 2, y: 46, icon: 'check', tone: 'positive', before: 'Can I get it?', strong: 'For sale, $1,950' },
            { x: 35, y: 77, icon: 'coins', tone: 'ink', before: 'Cost to hold', strong: '$2,088 over five years' },
            { x: 66, y: 84, icon: 'shield', tone: 'alert', before: 'Any risk?', strong: 'No live mark on the word' },
        ],
    },
    watch: {
        investor: [
            { x: 3, y: 39, icon: 'sparkles', strong: '8 new names', after: ' this morning' },
            { x: 38, y: 12, icon: 'gavel', tone: 'ink', strong: '3 closing today', after: ', first in 2h 36m' },
            { x: 70, y: 64, icon: 'droplet', tone: 'resell', strong: '2 dropping', after: ' this week' },
        ],
        founder: [
            { x: 6, y: 53, icon: 'sparkles', strong: '7 suggestions', after: ' to review today' },
            { x: 48, y: 21, icon: 'check', tone: 'positive', before: 'Finalists', strong: '2 of 3' },
            { x: 70, y: 41, icon: 'clock', tone: 'ink', strong: '12 days left', after: ', finalists due today' },
        ],
    },
    trend: {
        investor: [
            { x: 2, y: 56, icon: 'trending-up', tone: 'positive', strong: '25 trends', after: ', one name each' },
            { x: 30, y: 35, icon: 'check', strong: '14 open to register', after: ' from $12.98' },
            { x: 44, y: 57, icon: 'eye-off', tone: 'alert', strong: '3 gone', after: ' since yesterday' },
        ],
        founder: [
            { x: 3, y: 55, icon: 'trending-up', tone: 'positive', before: 'Padel', strong: '+62% in 12 months' },
            { x: 30, y: 62, icon: 'tag', strong: 'padel.app', after: ' open, $13.78 to register' },
            { x: 62, y: 50, icon: 'sparkles', tone: 'premium', before: 'Rating', strong: 'Strong choice' },
        ],
    },
    market: {
        investor: [
            { x: 2, y: 59, icon: 'clock', tone: 'ink', before: 'cove.io closes in', strong: '2h 36m' },
            { x: 44, y: 39, icon: 'gavel', strong: '3 bids jumped', after: ' since yesterday' },
            { x: 40, y: 83, icon: 'eye', tone: 'resell', strong: '9 to review', after: ', closing in the next 24 hours' },
        ],
        founder: [
            { x: 3, y: 40, icon: 'columns', tone: 'ink', strong: '3 finalists', after: ' side by side' },
            { x: 36, y: 57, icon: 'coins', tone: 'positive', before: 'anchor.co', strong: 'lowest first year, $24' },
            { x: 58, y: 28, icon: 'shield', tone: 'alert', before: 'harbor.io', strong: '3 live marks', after: ' on the word' },
        ],
    },
};

/** The tabs whose screen draws controls at its top right, where the "Sample screen" tag moves to the bottom right. */
export const HOME_DEMO_TAG_AT_BOTTOM: ReadonlySet<HomeDemoSampleTabId> = new Set(['check']);
